import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    // List-view heart relocation (NBB card layout): covers the
    // sessionStorage-restored initial state and, via the observer,
    // cards appended by infinite scroll while list view is active.
    // The observer only watches direct grid children, so moving hearts
    // inside an item never re-triggers it.
    this.#syncListHearts();
    const { grid } = this.refs;
    if (grid) {
      this.#heartObserver = new MutationObserver(() => this.#syncListHearts());
      this.#heartObserver.observe(grid, { childList: true });
    }
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    if (this.#heartObserver) this.#heartObserver.disconnect();
  }

  #heartObserver = null;

  /**
   * In list view the wishlist heart belongs in the card's top-right
   * corner, but it is rendered inside .card-gallery, whose
   * `container-type: inline-size` + `overflow: hidden` make a pure-CSS
   * escape impossible. So we re-parent the single <wishlist-heart> to
   * the .product-grid__item (position:relative in list view — the
   * existing absolute top/right CSS then lands it in the corner) and
   * move it back into its remembered gallery parent in any other view.
   */
  #syncListHearts() {
    const { grid } = this.refs;
    if (!grid) return;

    const isList = grid.getAttribute('product-grid-view') === 'list';

    for (const item of grid.querySelectorAll('.product-grid__item')) {
      const heart = item.querySelector('wishlist-heart');
      if (!heart) continue;

      if (isList) {
        if (heart.parentElement !== item) {
          heart.kxHomeParent = heart.parentElement;
          item.appendChild(heart);
        }
      } else if (heart.kxHomeParent && heart.parentElement === item) {
        heart.kxHomeParent.appendChild(heart);
      }
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
    this.#syncListHearts();
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
